import { isPlatformBrowser } from '@angular/common';
import { ApplicationRef, Injectable, NgZone, PLATFORM_ID, inject } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter, first, merge, timer } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AppUpdateService {
  private readonly RELOAD_THROTTLE_KEY = 'timeBank_last_app_update_reload';
  private readonly RELOAD_THROTTLE_MS = 60000;
  private readonly appRef = inject(ApplicationRef);
  private readonly swUpdate = inject(SwUpdate);
  private readonly zone = inject(NgZone);
  private readonly platformId = inject(PLATFORM_ID);
  private isReloading = false;
  private pendingReloadUntilOnline = false;

  initialize(): void {
    if (!isPlatformBrowser(this.platformId) || !this.swUpdate.isEnabled) return;

    this.reloadWhenVersionIsReady();
    this.reloadWhenServiceWorkerCannotRecover();
    this.checkAfterStartup();
    this.checkWhenAppReturnsToForeground();
    this.checkWhenNetworkReturns();
  }

  private reloadWhenVersionIsReady(): void {
    this.swUpdate.versionUpdates
      .pipe(filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'))
      .subscribe(() => this.reloadForLatestVersion());
  }

  private reloadWhenServiceWorkerCannotRecover(): void {
    this.swUpdate.unrecoverable.subscribe(() => this.reloadForLatestVersion());
  }

  private checkAfterStartup(): void {
    const appStable$ = this.appRef.isStable.pipe(first((isStable) => isStable));
    const startupFallback$ = timer(5000);

    merge(appStable$, startupFallback$)
      .pipe(first())
      .subscribe(() => void this.checkForUpdate());
  }

  private checkWhenAppReturnsToForeground(): void {
    this.zone.runOutsideAngular(() => {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          void this.checkForUpdate();
        }
      });
    });
  }

  private checkWhenNetworkReturns(): void {
    this.zone.runOutsideAngular(() => {
      window.addEventListener('online', () => {
        if (this.pendingReloadUntilOnline) {
          this.reloadForLatestVersion();
          return;
        }

        void this.checkForUpdate();
      });
    });
  }

  private async checkForUpdate(): Promise<void> {
    try {
      await this.swUpdate.checkForUpdate();
    } catch (error) {
      console.warn('檢查新版 Web App 失敗:', error);
    }
  }

  private reloadForLatestVersion(): void {
    if (this.isReloading) return;
    if (isPlatformBrowser(this.platformId) && !navigator.onLine) {
      this.pendingReloadUntilOnline = true;
      return;
    }
    if (this.wasReloadedRecently()) return;

    this.isReloading = true;
    this.pendingReloadUntilOnline = false;
    sessionStorage.setItem(this.RELOAD_THROTTLE_KEY, String(Date.now()));
    location.reload();
  }

  private wasReloadedRecently(): boolean {
    const lastReload = Number(sessionStorage.getItem(this.RELOAD_THROTTLE_KEY) || 0);
    return Number.isFinite(lastReload) && Date.now() - lastReload < this.RELOAD_THROTTLE_MS;
  }
}
